import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Yup from 'yup'

// @material-ui
import { Button, Grid } from '@material-ui/core'
import { withStyles } from '@material-ui/styles'

// core
import { withLang } from '_core/hocs/withLang'
import { useGlobal } from '_core/hooks/useGlobal'
import { withVisitorInfo } from '_core/utils/visitorInfo'
import { FormValidator } from '_core/components/FormValidator'
import { TextInput } from '_core/components/TextInput'
import { FileInput } from '_core/components/FileInput'

// style
import { verifyPassportStepFormStyle } from './verifyPassportStepFormStyle'

const RawVerifyPassportStepForm = (props) => {
  const { classes, update, visitorInfo, getLangString: l } = props

  const {
    request: { queue },
    notify: { enqueueSnackbar },
    config: { API_METHOD, VERIFICATION_TYPE },
  } = useGlobal()

  const [values, setValues] = useState({
    lastname: visitorInfo.info.lastname,
    firstname: visitorInfo.info.firstname,
    patronymic: visitorInfo.info.patronymic,
    passp_serial: visitorInfo.info.passp_serial,
    passp_number: visitorInfo.info.passp_number,
  })

  const getOnChange = field => value => {
    setValues(oldValues => ({
      ...oldValues,
      [field]: value,
    }))
  }

  const onSubmit = async event => {
    event.preventDefault()

    try {
      await queue(API_METHOD.VISITOR_VERIFICATION_START, {
        type: VERIFICATION_TYPE.PASSPORT,
        ...values,
      })

      update()
    } catch (error) {
      process.env.NODE_ENV !== 'production' && console.error(error)
      enqueueSnackbar(error.message, { variant: 'error' })
    }
  }

  const fields = {
    lastname: {
      value: values['lastname'],
      label: l('r._.profile.verify.passport.label.lastname'),
      onChange: getOnChange('lastname'),
      shape: Yup.string(l('r._.profile.verify.passport.label.lastname'))
        .trim()
        .ensure()
        .required('empty')
        .min(1, 'short')
        .max(50, 'long'),
    },
    firstname: {
      value: values['firstname'],
      label: l('r._.profile.verify.passport.label.firstname'),
      onChange: getOnChange('firstname'),
      shape: Yup.string(l('r._.profile.verify.passport.label.firstname'))
        .trim()
        .ensure()
        .required('empty')
        .min(1, 'short')
        .max(50, 'long'),
    },
    patronymic: {
      value: values['patronymic'],
      label: l('r._.profile.verify.passport.label.patronymic'),
      onChange: getOnChange('patronymic'),
      shape: Yup.string(l('r._.profile.verify.passport.label.patronymic'))
        .trim()
        .ensure()
        .required('empty')
        .min(1, 'short')
        .max(50, 'long'),
    },
    passp_serial: {
      value: values['passp_serial'],
      label: l('r._.profile.verify.passport.label.serial'),
      onChange: getOnChange('passp_serial'),
      // ban: /[^-\s0-9]/g,
      shape: Yup.string(l('r._.profile.verify.passport.label.serial'))
        .trim()
        .ensure()
        .required('empty'),
      // .min(2, 'short')
      // .max(6, 'long'),
    },
    passp_number: {
      value: values['passp_number'],
      label: l('r._.profile.verify.passport.label.number'),
      onChange: getOnChange('passp_number'),
      // ban: /[^-\s0-9]/g,
      shape: Yup.string(l('r._.profile.verify.passport.label.number'))
        .trim()
        .ensure()
        .required('empty'),
      // .min(4, 'short')
      // .max(10, 'long'),
    },
    note: {
      value: values['note'],
      label: l('r._.profile.verify.passport.label.note'),
      onChange: getOnChange('note'),
      shape: Yup.string(l('r._.profile.verify.passport.label.note'))
        .trim()
        .ensure()
        .max(2000, 'long'),
    },
    files: {
      value: values['files'],
      label: l('r._.profile.verify.passport.label.files'),
      onChange: getOnChange('files'),
      getEventData: files => ({
        value: files.map(file => file.id),
      }),
      shape: Yup.array(
        Yup.object({
          id: Yup.number().required().positive().integer(),
        })
      )
        .ensure()
        .required('empty')
        .min(1, 'short')
        .max(5, 'long'),
    },
  }

  return (
    <form onSubmit={onSubmit} className={classes.root}>
      <FormValidator
        fields={fields}
        method={`${API_METHOD.VISITOR_VERIFICATION_START}/${VERIFICATION_TYPE.PASSPORT}`}
        render={({ form, canSubmit }) => (
          <Grid container={true}>
            <Grid item={true} xs={12} sm={4}>
              <TextInput {...form['lastname']} />
            </Grid>
            <Grid item={true} xs={12} sm={4}>
              <TextInput {...form['firstname']} />
            </Grid>
            <Grid item={true} xs={12} sm={4}>
              <TextInput {...form['patronymic']} />
            </Grid>
            <Grid item={true} xs={12} sm={6}>
              <TextInput {...form['passp_serial']} />
            </Grid>
            <Grid item={true} xs={12} sm={6}>
              <TextInput {...form['passp_number']} />
            </Grid>
            <Grid item={true} xs={12}>
              <TextInput {...form['note']} multiline={true} />
            </Grid>
            <Grid item={true} xs={12} sm={6}>
              <FileInput
                {...form['files']}
                acceptScan={true}
                category="verifpassport"
              />
            </Grid>

            <Grid item={true} xs={12} sm={2}></Grid>

            <Grid item={true} xs={12} sm={4}>
              <Button type="submit" fullWidth={true} disabled={!canSubmit}>
                {l('r._.profile.verify.control.submit')}
              </Button>
            </Grid>
          </Grid>
        )}
      />
    </form>
  )
};

RawVerifyPassportStepForm.defaultProps = {}

RawVerifyPassportStepForm.propTypes = {
  // self props
  update: PropTypes.func.isRequired,

  // withVisitorInfo
  visitorInfo: PropTypes.object.isRequired,

  // `withStyles` HOC props
  classes: PropTypes.object.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
}

export const VerifyPassportStepForm = withVisitorInfo(withLang(
  withStyles(verifyPassportStepFormStyle)(RawVerifyPassportStepForm)
))
